import React, { useRef } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { AgreementService } from 'src/service/AgreementService';

interface Step1BasicInfoProps {
    formData: any;
    setFormData: (data: any | ((prev: any) => any)) => void;
    documentTypes: any[];
    isViewMode?: boolean;
    validationTriggered?: boolean;
    touchedFields?: Record<string, boolean>;
    markDirty?: () => void;
    isTemplateMode?: boolean;
    onAutoLoadTemplate?: (templateId: string) => void;
}

export const Step1BasicInfo: React.FC<Step1BasicInfoProps> = ({
    formData,
    setFormData,
    documentTypes,
    isViewMode = false,
    validationTriggered = false,
    touchedFields = {},
    markDirty,
    isTemplateMode = false,
    onAutoLoadTemplate
}) => {
    const effectiveDateCalendarRef = useRef<any>(null);
    const expiryDateCalendarRef = useRef<any>(null);
    const isImportant = !!formData.isImportant;
    const isUrgent = !!formData.isUrgent;

    const handleInputChange = (field: string, value: any, options?: { skipTouch?: boolean; skipAutoCode?: boolean }) => {
        let newData = { ...formData, [field]: value };

        if (field === 'agreementType' && formData.agreementType !== value) {
            newData = { ...newData, agreementNo: '' };
        }

        setFormData(newData);

        if (field === 'agreementType' && formData.agreementType !== value && !options?.skipAutoCode) {
            generateDocumentCode(value);
        }

        if (markDirty) markDirty();
    };

    const generateDocumentCode = async (typeId: string) => {
        if (!typeId) return;
        try {
            const res = await AgreementService.generateDocumentCode(typeId);
            if (res && res.agreementNo) {
                setFormData((prev: any) => ({
                    ...prev,
                    agreementType: typeId,
                    agreementNo: res.agreementNo,
                    agreementNoInt: res.agreementNoInt || null,
                    agreementNoFormatEnumId: res.agreementNoFormatEnumId || null
                }));

                // Nếu có template ID thì trigger load thông tin từ mẫu
                if (res.templateAgreementId && onAutoLoadTemplate && isTemplateMode) {
                    onAutoLoadTemplate(res.templateAgreementId);
                }
            }
        } catch (e) {
            console.error('Error generating document code', e);
        }
    };

    const getEffectiveMaxDate = () => {
        const today = new Date();
        if (formData.expiryDate) {
            const expiry = new Date(formData.expiryDate as any);
            return expiry < today ? expiry : today;
        }
        return today;
    };

    const getExpiryMinDate = () => {
        const now = new Date();
        const nowPlusOneHour = new Date(now.getTime() + 60 * 60 * 1000);
        if (formData.effectiveDate) {
            const effectivePlusOneHour = new Date(new Date(formData.effectiveDate as any).getTime() + 60 * 60 * 1000);
            return new Date(Math.max(nowPlusOneHour.getTime(), effectivePlusOneHour.getTime()));
        }
        return nowPlusOneHour;
    };

    return (
        <div className="flex flex-column gap-3">
            {/* Document Marking */}
            <div className="field mb-3">
                <div className="text-sm">
                    <div className="mb-2">Mức độ quan trọng tài liệu:</div>
                    <div className="flex align-items-center gap-3">
                    <button
                        type="button"
                        className={`document-priority-option ${isImportant ? 'text-orange-500 font-semibold' : 'text-500'}`}
                        style={{ border: 0, background: 'transparent', padding: 0, font: 'inherit', cursor: isViewMode ? 'default' : 'pointer' }}
                        onClick={() => handleInputChange('isImportant', !isImportant)}
                        disabled={isViewMode}
                    >
                        <i className="pi pi-bookmark-fill mr-1" />Quan trọng
                    </button>
                    <button
                        type="button"
                        className={`document-priority-option ${isUrgent ? 'text-red-500 font-semibold' : 'text-500'}`}
                        style={{ border: 0, background: 'transparent', padding: 0, font: 'inherit', cursor: isViewMode ? 'default' : 'pointer' }}
                        onClick={() => handleInputChange('isUrgent', !isUrgent)}
                        disabled={isViewMode}
                    >
                        <i className="pi pi-bolt mr-1" />Khẩn cấp
                    </button>
                    </div>
                </div>
            </div>

            {/* Document Name */}
            <div className="field mb-3">
                <label htmlFor="agreementName" className="block font-medium mb-1">
                    Tên tài liệu <span className="text-red-500">(*)</span>
                </label>
                <InputText
                    id="agreementName"
                    value={formData.agreementName}
                    onChange={(e) => handleInputChange('agreementName', e.target.value)}
                    className={`w-full ${(!formData.agreementName.trim() && (validationTriggered || touchedFields.agreementName)) ? 'p-invalid' : ''}`}
                    placeholder="Nhập tên tài liệu"
                    disabled={isViewMode}
                    size="small"
                />
                {!formData.agreementName.trim() && (validationTriggered || touchedFields.agreementName) && (
                    <small className="p-error text-xs">Không được để trống</small>
                )}
            </div>

            {/* Document Type */}
            <div className="field mb-3">
                <label htmlFor="agreementType" className="block font-medium mb-1">
                    Loại tài liệu <span className="text-red-500">(*)</span>
                </label>
                <Dropdown
                    id="agreementType"
                    value={formData.agreementType}
                    options={documentTypes}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(e) => handleInputChange('agreementType', e.value)}
                    placeholder="Chọn loại tài liệu"
                    className={`w-full ${!formData.agreementType && (validationTriggered || touchedFields.agreementType) ? 'p-invalid' : ''}`}
                    disabled={isViewMode || isTemplateMode}
                    filter
                />
            </div>

            {/* Date Fields */}
            <div className="field mb-3">
                <div className="grid">
                    <div className="col-12">
                        <label htmlFor="effectiveDate" className="block font-medium mb-1">Ngày hiệu lực</label>
                        <Calendar
                            ref={effectiveDateCalendarRef}
                            id="effectiveDate"
                            value={formData.effectiveDate}
                            onChange={(e) => handleInputChange('effectiveDate', e.value)}
                            placeholder="Chọn ngày"
                            showIcon
                            dateFormat="dd/mm/yy"
                            showTime
                            hourFormat="24"
                            maxDate={getEffectiveMaxDate()}
                            disabled={isViewMode}
                            className="w-full h-3rem"
                        />
                    </div>
                    <div className="col-12">
                        <label htmlFor="expiryDate" className="block font-medium mb-1">Ngày hết hiệu lực</label>
                        <Calendar
                            ref={expiryDateCalendarRef}
                            id="expiryDate"
                            value={formData.expiryDate}
                            onChange={(e) => handleInputChange('expiryDate', e.value)}
                            placeholder="Chọn ngày"
                            showIcon
                            dateFormat="dd/mm/yy"
                            showTime
                            hourFormat="24"
                            minDate={getExpiryMinDate()}
                            disabled={isViewMode}
                            className="w-full h-3rem"
                        />
                    </div>
                </div>
            </div>

            {/* Document Code */}
            <div className="field mb-3">
                <label htmlFor="agreementNo" className="block font-medium mb-1">Mã tài liệu <span className="text-red-500">(*)</span></label>
                <div className="p-inputgroup">
                    <InputText
                        id="agreementNo"
                        value={formData.agreementNo}
                        onChange={(e) => handleInputChange('agreementNo', e.target.value)}
                        placeholder="Mã tài liệu"
                        disabled={isViewMode}
                        size="small"
                        className={!formData.agreementNo && (validationTriggered || touchedFields.agreementNo) ? 'p-invalid' : ''}
                    />
                    <Button
                        type="button"
                        icon="pi pi-refresh"
                        onClick={() => generateDocumentCode(formData.agreementType)}
                        disabled={isViewMode || !formData.agreementType}
                        tooltip="Tạo mã tự động"
                        className="p-button-outlined"
                    />
                </div>
            </div>

            {/* Description */}
            <div className="field mb-3">
                <label htmlFor="description" className="block font-medium mb-1">Mô tả</label>
                <InputTextarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={5}
                    placeholder="Mô tả tài liệu"
                    className="w-full"
                    disabled={isViewMode}
                    autoResize
                />
            </div>
        </div>
    );
};
